const API_BASE = '/api'

async function authFetch(path: string, options?: RequestInit): Promise<Response> {
  const res = await fetch(`${API_BASE}${path}`, { credentials: 'include', ...options })
  if (res.status === 401) {
    window.location.href = '/auth/login'
    throw new Error('Session expired')
  }
  return res
}

async function failFriendly(res: Response, fallback: string): Promise<never> {
  let msg = fallback
  try {
    const body = await res.json()
    if (body?.error && typeof body.error === 'string') msg = body.error
  } catch {
    void 0
  }
  throw new Error(msg)
}

function appendListParam(params: URLSearchParams, key: string, vals?: string[]) {
  if (!vals) return
  for (const v of vals) {
    if (v) params.append(key, v)
  }
}

export type TradingMode = 'agent' | 'algo'

// --- Trader / paper trading per-thesis -----------------------------------

export type ApiThesisOrder = {
  id: string
  product: 'rdbee' | 'rtpe' | 'rdn'
  contract_code: string
  side: 'buy' | 'sell'
  execution_mode: 'paper'
  paper_group_id?: string | null
  size_mwh: number
  status: 'open' | 'filled' | 'partial' | 'cancelled' | 'expired'
  submitted_at: string
  simulated_submitted_at?: string | null
  simulated_expires_at?: string | null
  delivery_date?: string | null
  realistic_trade_date?: string | null
  paper_trade_date?: string | null
  cancelled_at: string | null
  created_by: 'user' | 'trader'
}

export type ApiThesisFill = {
  id: string
  order_id: string
  product: 'rdbee' | 'rtpe' | 'rdn'
  contract_code: string
  side: 'buy' | 'sell'
  fill_price_pln: number
  fill_size_mwh: number
  filled_at: string
  market_filled_at?: string
  source_trade_id: string | null
}

export type ApiThesisPosition = {
  product: 'rdbee' | 'rtpe' | 'rdn'
  contract_code: string
  net_size_mwh: number
  avg_buy_price_pln: number
  avg_sell_price_pln: number
  realized_pnl_pln: number
  mark_price_pln: number | null
  mark_observed_at: string | null
  unrealized_pnl_pln: number | null
}

export type ApiThesisTradesTotals = {
  realized_pnl_pln: number
  unrealized_pnl_pln: number
  total_pnl_pln: number
}

export type ApiThesisTrades = {
  thesis_id: string
  orders: ApiThesisOrder[]
  fills: ApiThesisFill[]
  positions: ApiThesisPosition[]
  totals: ApiThesisTradesTotals
}

export async function fetchThesisTrades(thesisId: string): Promise<ApiThesisTrades> {
  const res = await authFetch(`/theses/${thesisId}/trades`)
  if (!res.ok) throw new Error(`Failed to fetch trades: ${res.status}`)
  return res.json()
}

export function thesisTradesCSVUrl(thesisId: string): string {
  return `${API_BASE}/theses/${thesisId}/trades.csv`
}

// --- Cross-thesis trades + runs feed (/trades page) ----------------------

export interface ApiTradeRow {
  id: string
  thesis_id: string | null
  thesis_name: string | null
  product: string
  contract_code: string
  side: string
  execution_mode: 'paper'
  paper_group_id?: string | null
  size_mwh: number
  status: string
  submitted_at: string
  simulated_submitted_at?: string | null
  expires_at: string | null
  simulated_expires_at?: string | null
  delivery_date?: string | null
  realistic_trade_date?: string | null
  paper_trade_date?: string | null
  cancelled_at: string | null
  created_by: string
  filled_mwh: number
  avg_fill_px: number | null
  fill_count: number
  last_filled_at: string | null
}

export interface ApiTradesListFilters {
  thesis_id?: string[]
  product?: string[]
  status?: string[]
  contract?: string
  from?: string
  to?: string
  limit?: number
  offset?: number
}

export async function fetchTradesList(
  filters: ApiTradesListFilters = {},
): Promise<{ trades: ApiTradeRow[]; count: number; limit: number; offset: number }> {
  const params = new URLSearchParams()
  appendListParam(params, 'thesis_id', filters.thesis_id)
  appendListParam(params, 'product', filters.product)
  appendListParam(params, 'status', filters.status)
  if (filters.contract) params.set('contract', filters.contract)
  if (filters.from) params.set('from', filters.from)
  if (filters.to) params.set('to', filters.to)
  if (filters.limit != null) params.set('limit', String(filters.limit))
  if (filters.offset != null) params.set('offset', String(filters.offset))
  const qs = params.toString()
  const res = await authFetch(`/trades${qs ? `?${qs}` : ''}`)
  if (!res.ok) throw new Error(`Failed to fetch trades: ${res.status}`)
  return res.json()
}

export interface ApiScriptRunRow {
  id: string
  script_id: string
  thesis_id: string
  thesis_name: string | null
  slot_ts: string
  market_ts: string
  started_at: string
  finished_at: string | null
  exit_code: number | null
  stdout_len: number
  stderr_len: number
  order_count: number
}

export interface ApiScriptRunsListFilters {
  thesis_id?: string[]
  from?: string
  to?: string
  errored_only?: boolean
  limit?: number
  offset?: number
}

export async function fetchScriptRunsList(
  filters: ApiScriptRunsListFilters = {},
): Promise<{ runs: ApiScriptRunRow[]; count: number; limit: number; offset: number }> {
  const params = new URLSearchParams()
  appendListParam(params, 'thesis_id', filters.thesis_id)
  if (filters.from) params.set('from', filters.from)
  if (filters.to) params.set('to', filters.to)
  if (filters.errored_only) params.set('errored_only', 'true')
  if (filters.limit != null) params.set('limit', String(filters.limit))
  if (filters.offset != null) params.set('offset', String(filters.offset))
  const qs = params.toString()
  const res = await authFetch(`/script-runs${qs ? `?${qs}` : ''}`)
  if (!res.ok) throw new Error(`Failed to fetch script runs: ${res.status}`)
  return res.json()
}

export interface ApiScriptRunDetail extends ApiScriptRunRow {
  stdout: string
  stderr: string
  orders: Array<{
    id: string
    product: string
    contract_code: string
    side: string
    execution_mode: 'paper'
    paper_group_id?: string | null
    size_mwh: number
    status: string
  }>
}

export async function fetchScriptRun(id: string): Promise<ApiScriptRunDetail> {
  const res = await authFetch(`/script-runs/${id}`)
  if (!res.ok) throw new Error(`Failed to fetch run: ${res.status}`)
  return res.json()
}

export interface ApiFillRow {
  id: string
  order_id: string
  thesis_id: string | null
  thesis_name: string | null
  product: string
  contract_code: string
  side: string
  fill_price_pln: number
  fill_size_mwh: number
  filled_at: string
  market_filled_at?: string
  source_trade_id: string | null
}

export interface ApiFillsListFilters {
  thesis_id?: string[]
  product?: string[]
  contract?: string
  from?: string
  to?: string
  limit?: number
  offset?: number
}

export async function fetchFillsList(
  filters: ApiFillsListFilters = {},
): Promise<{ fills: ApiFillRow[]; count: number; limit: number; offset: number }> {
  const params = new URLSearchParams()
  appendListParam(params, 'thesis_id', filters.thesis_id)
  appendListParam(params, 'product', filters.product)
  if (filters.contract) params.set('contract', filters.contract)
  if (filters.from) params.set('from', filters.from)
  if (filters.to) params.set('to', filters.to)
  if (filters.limit != null) params.set('limit', String(filters.limit))
  if (filters.offset != null) params.set('offset', String(filters.offset))
  const qs = params.toString()
  const res = await authFetch(`/fills${qs ? `?${qs}` : ''}`)
  if (!res.ok) throw new Error(`Failed to fetch fills: ${res.status}`)
  return res.json()
}

export interface ApiDailyPnLLeg {
  contract_code: string
  side: string
  filled_mwh: number
  avg_fill_px: number
}

export interface ApiDailyPnLGroup {
  group_id: string
  product: string
  delivery_date?: string | null
  realistic_trade_date?: string | null
  paper_trade_date?: string | null
  buy_mwh: number
  sell_mwh: number
  pnl_pln: number
  last_filled_at: string
  legs: ApiDailyPnLLeg[]
}

export interface ApiDailyPnLRow {
  thesis_id: string
  thesis_name: string | null
  day: string
  realized_pnl_pln: number
  cumulative_pnl_pln: number
  completed_groups: number
  open_groups: number
  groups: ApiDailyPnLGroup[]
}

export interface ApiDailyPnLFilters {
  thesis_id?: string[]
  product?: string[]
  contract?: string
  from?: string
  to?: string
}

export async function fetchDailyPnL(
  filters: ApiDailyPnLFilters = {},
): Promise<{ daily_pnl: ApiDailyPnLRow[]; count: number }> {
  const params = new URLSearchParams()
  appendListParam(params, 'thesis_id', filters.thesis_id)
  appendListParam(params, 'product', filters.product)
  if (filters.contract) params.set('contract', filters.contract)
  if (filters.from) params.set('from', filters.from)
  if (filters.to) params.set('to', filters.to)
  const qs = params.toString()
  const res = await authFetch(`/pnl/daily${qs ? `?${qs}` : ''}`)
  if (!res.ok) throw new Error(`Failed to fetch daily P&L: ${res.status}`)
  return res.json()
}

export interface ApiPositionRow {
  product: string
  contract_code: string
  net_size_mwh: number
  avg_buy_price_pln: number
  avg_sell_price_pln: number
  realized_pnl_pln: number
  mark_price_pln: number | null
  mark_observed_at: string | null
  unrealized_pnl_pln: number | null
}

export interface ApiPositionsResponse {
  positions: ApiPositionRow[]
  totals: {
    realized_pnl_pln: number
    unrealized_pnl_pln: number
    total_pnl_pln: number
  }
  count: number
}

export interface ApiPositionsListFilters {
  thesis_id?: string[]
  product?: string[]
  contract?: string
}

export async function fetchPositions(
  filters: ApiPositionsListFilters = {},
): Promise<ApiPositionsResponse> {
  const params = new URLSearchParams()
  appendListParam(params, 'thesis_id', filters.thesis_id)
  appendListParam(params, 'product', filters.product)
  if (filters.contract) params.set('contract', filters.contract)
  const qs = params.toString()
  const res = await authFetch(`/positions${qs ? `?${qs}` : ''}`)
  if (!res.ok) throw new Error(`Failed to fetch positions: ${res.status}`)
  return res.json()
}

export interface ApiExposureLeg {
  contract_code: string
  side: string
  net_mwh: number
  avg_price_pln: number | null
  mark_price_pln: number | null
  unrealized_pnl_pln: number | null
}

export interface ApiExposureGroup {
  market_day: string
  group_id: string
  product: string
  legs: ApiExposureLeg[]
  net_mwh: number
  unrealized_pnl_pln: number | null
  status: string
  delivery_date?: string | null
  realistic_trade_date?: string | null
  paper_trade_date?: string | null
}

export async function fetchExposure(
  filters: ApiPositionsListFilters = {},
): Promise<{ exposure: ApiExposureGroup[]; count: number }> {
  const params = new URLSearchParams()
  appendListParam(params, 'thesis_id', filters.thesis_id)
  appendListParam(params, 'product', filters.product)
  if (filters.contract) params.set('contract', filters.contract)
  const qs = params.toString()
  const res = await authFetch(`/exposure${qs ? `?${qs}` : ''}`)
  if (!res.ok) throw new Error(`Failed to fetch exposure: ${res.status}`)
  return res.json()
}

export interface ApiIntervalExposureRow {
  delivery_interval: string
  thesis_id?: string
  thesis_name?: string | null
  net_mwh: number
  intent_mwh: number
  cash_flow_pln: number
  realized_pnl_pln: number
  mark_price_pln?: number | null
  unrealized_pnl_pln?: number | null
  status: string
  markets: string[]
  instruments: string[]
  legs?: ApiIntervalExposureLeg[]
  close_deadline: string
}

export interface ApiIntervalExposureLeg {
  product: string
  contract_code: string
  side: 'buy' | 'sell' | string
  mwh: number
  source: 'fill' | 'intent' | string
}

export interface ApiExposureIntervalsFilters extends ApiPositionsListFilters {
  status?: string[]
  group_by?: 'thesis'
}

export interface ApiExposureIntervalsResponse {
  intervals: ApiIntervalExposureRow[]
  count: number
  truncated: boolean
  total_net_open_mwh: number
  total_cash_flow_pln: number
  total_realized_pln: number
  status_counts: Record<string, number>
}

export async function fetchExposureIntervals(
  filters: ApiExposureIntervalsFilters = {},
): Promise<ApiExposureIntervalsResponse> {
  const params = new URLSearchParams()
  appendListParam(params, 'thesis_id', filters.thesis_id)
  appendListParam(params, 'product', filters.product)
  appendListParam(params, 'status', filters.status)
  if (filters.contract) params.set('contract', filters.contract)
  if (filters.group_by) params.set('group_by', filters.group_by)
  const qs = params.toString()
  const res = await authFetch(`/exposure/intervals${qs ? `?${qs}` : ''}`)
  if (!res.ok) throw new Error(`Failed to fetch interval exposure: ${res.status}`)
  return res.json()
}

// --- Script runs (algo-mode theses) --------------------------------------

export type ApiThesisScriptRun = {
  id: string
  script_id: string
  slot_ts: string
  started_at: string
  finished_at?: string
  exit_code?: number
  stdout: string
  stderr: string
}

export type ApiThesisScriptRuns = {
  thesis_id: string
  runs: ApiThesisScriptRun[]
}

export async function fetchThesisScriptRuns(
  thesisId: string,
  limit = 50,
): Promise<ApiThesisScriptRuns> {
  const res = await authFetch(`/theses/${thesisId}/script-runs?limit=${limit}`)
  if (!res.ok) throw new Error(`Failed to fetch script runs: ${res.status}`)
  return res.json()
}

// --- Trader scripts ------------------------------------------------------

export interface ApiTraderScript {
  id: string
  thesis_id: string
  thesis_name: string
  thesis_status: string
  enabled: boolean
  created_at: string
  script?: string
}

export async function fetchTraderScripts(): Promise<ApiTraderScript[]> {
  const res = await authFetch(`/trader-scripts`)
  if (!res.ok) throw new Error(`Failed to fetch trader scripts: ${res.status}`)
  const data = await res.json()
  return data.scripts ?? []
}

export async function fetchTraderScript(id: string): Promise<ApiTraderScript> {
  const res = await authFetch(`/trader-scripts/${id}`)
  if (!res.ok) throw new Error(`Failed to fetch trader script: ${res.status}`)
  return res.json()
}

export async function patchTraderScript(
  id: string,
  patch: { enabled?: boolean; script?: string },
): Promise<void> {
  const res = await authFetch(`/trader-scripts/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  })
  if (!res.ok) await failFriendly(res, "Couldn't update trader script.")
}

export async function deleteTraderScript(id: string): Promise<void> {
  const res = await authFetch(`/trader-scripts/${id}`, { method: 'DELETE' })
  if (!res.ok) await failFriendly(res, "Couldn't delete trader script.")
}

export async function createTraderScript(thesisId: string, script: string): Promise<{ id: string }> {
  const res = await authFetch(`/theses/${thesisId}/trader-scripts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ script }),
  })
  if (!res.ok) await failFriendly(res, "Couldn't upload trader script.")
  return res.json()
}
